/**
 * ScoreHistoryChart — Shared component for score trend line chart.
 *
 * Used by:
 *   Training Portal: /app/history
 *   Admin Portal:    /admin/agents/{id}
 *
 * Renders a Recharts LineChart with one point per session date.
 * A reference line at y=0.7 marks the pass threshold.
 *
 * EPIC-04-05 AC-2, AC-6
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';

export interface ScoreTrendPoint {
  date: string;    // YYYY-MM-DD
  score: number;   // 0.0 – 1.0
}

export interface ScoreHistoryChartProps {
  data: ScoreTrendPoint[];
  passThreshold?: number;   // default 0.7
  height?: number;          // default 220
  i18nNs?: string;          // 'history' (default) — for Training Portal
}

export function ScoreHistoryChart({
  data,
  passThreshold = 0.7,
  height = 220,
  i18nNs = 'history',
}: ScoreHistoryChartProps): JSX.Element {
  const { t } = useTranslation(i18nNs);

  if (data.length < 2) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#888',
          fontSize: '0.9rem',
          border: '1px dashed #ccc',
          borderRadius: 8,
        }}
        data-testid="score-chart-empty"
      >
        {t('noSessions')}
      </div>
    );
  }

  const chartData = data.map((p) => ({
    ...p,
    scorePercent: Math.round(p.score * 100),
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11 }}
          tickFormatter={(v: string) => v.slice(5)}  // MM-DD display
        />
        <YAxis
          domain={[0, 100]}
          tickFormatter={(v: number) => `${v}%`}
          tick={{ fontSize: 11 }}
        />
        <Tooltip
          formatter={(value) => [`${value}%`, t('chartScore')]}
        />
        <ReferenceLine
          y={passThreshold * 100}
          stroke="#e74c3c"
          strokeDasharray="4 2"
          label={{
            value: t('chartPassLine'),
            position: 'right',
            fontSize: 11,
            fill: '#e74c3c',
          }}
        />
        <Line
          type="monotone"
          dataKey="scorePercent"
          stroke="#2563eb"
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
